import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTeam } from 'team/contexts/teamContext'
import {
    getIntegrations,
    getTeamIntegrations,
    getSubteamIntegrations,
    connectTeamIntegration,
    connectIntegration,
    disconnectTeamIntegration,
    disconnectIntegration
} from 'team/apis/teamIntegrationApi'

/**
 * Custom hook to manage integrations (both team and subteam)
 * @param {string} subteamId - Optional subteam ID
 * @param {Object} team - Optional team object
 * @returns {Object} Integration state and handlers
 */
export const useIntegrations = (subteamId = null, team = null) => {
    const [searchParams] = useSearchParams()
    const { currentTeamId } = useTeam()
    const [searchTerm, setSearchTerm] = useState('')
    const [availableIntegrations, setAvailableIntegrations] = useState([])
    const [connectedIntegrations, setConnectedIntegrations] = useState([])
    const [loading, setLoading] = useState(false)
    const [selectedIntegration, setSelectedIntegration] = useState(null)
    const [connectionModalOpen, setConnectionModalOpen] = useState(false)
    const [connecting, setConnecting] = useState(false)
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success'
    })

    // Fetch available and connected integrations
    const fetchIntegrations = useCallback(async () => {
        const teamId = currentTeamId || team?._id

        console.log('[TeamIntegrationsContainer] fetchIntegrations called', { teamId, subteamId, currentTeamId })

        try {
            setLoading(true)

            // Always fetch available integrations (they're global)
            console.log('[TeamIntegrationsContainer] Fetching available integrations...')
            const availableRes = await getIntegrations()
            console.log('[TeamIntegrationsContainer] Available integrations response:', availableRes)
            console.log('[TeamIntegrationsContainer] Response type:', typeof availableRes)
            console.log('[TeamIntegrationsContainer] Response keys:', availableRes ? Object.keys(availableRes) : 'null')

            // Handle different response structures
            let integrations = []
            if (Array.isArray(availableRes)) {
                integrations = availableRes
            } else if (availableRes?.integrations && Array.isArray(availableRes.integrations)) {
                integrations = availableRes.integrations
            } else if (availableRes?.data && Array.isArray(availableRes.data)) {
                integrations = availableRes.data
            } else if (availableRes?.success && Array.isArray(availableRes.data)) {
                integrations = availableRes.data
            }

            console.log('[TeamIntegrationsContainer] Parsed integrations:', integrations)
            setAvailableIntegrations(integrations)

            // Fetch connected integrations only if we have teamId or subteamId
            if (subteamId) {
                console.log('[TeamIntegrationsContainer] Fetching subteam integrations...')
                const connectedRes = await getSubteamIntegrations(subteamId)
                console.log('[TeamIntegrationsContainer] Connected subteam integrations:', connectedRes)
                const connected = Array.isArray(connectedRes) ? connectedRes : (connectedRes?.integrations || connectedRes?.data || [])
                setConnectedIntegrations(connected)
            } else if (teamId) {
                console.log('[TeamIntegrationsContainer] Fetching team integrations...')
                const connectedRes = await getTeamIntegrations(teamId)
                console.log('[TeamIntegrationsContainer] Connected team integrations:', connectedRes)
                const connected = Array.isArray(connectedRes) ? connectedRes : (connectedRes?.integrations || connectedRes?.data || [])
                setConnectedIntegrations(connected)
            } else {
                // No team/subteam ID yet, but we can still show available integrations
                console.log('[TeamIntegrationsContainer] No team/subteam ID, setting empty connected integrations')
                setConnectedIntegrations([])
            }
        } catch (error) {
            console.error('[TeamIntegrationsContainer] Error fetching integrations:', error)
            console.error('[TeamIntegrationsContainer] Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            })
            setNotification({
                open: true,
                message: error.response?.data?.message || 'Failed to load integrations. Please check console for details.',
                severity: 'error'
            })
        } finally {
            setLoading(false)
        }
    }, [currentTeamId, team, subteamId])

    // Check for OAuth callback
    useEffect(() => {
        const connected = searchParams.get('connected')
        const integration = searchParams.get('integration')
        const error = searchParams.get('error')

        if (connected === 'true' && integration) {
            setNotification({
                open: true,
                message: `Successfully connected to ${integration}!`,
                severity: 'success'
            })
            window.history.replaceState({}, '', window.location.pathname)
            fetchIntegrations()
        } else if (error === 'oauth_failed') {
            setNotification({
                open: true,
                message: 'Failed to connect integration. Please try again.',
                severity: 'error'
            })
            window.history.replaceState({}, '', window.location.pathname)
        }
    }, [searchParams, fetchIntegrations])

    useEffect(() => {
        console.log('[TeamIntegrationsContainer] useEffect triggered, calling fetchIntegrations')
        console.log('[TeamIntegrationsContainer] Component mounted/updated')
        console.log('[TeamIntegrationsContainer] Current state:', {
            availableIntegrationsCount: availableIntegrations.length,
            loading,
            currentTeamId,
            subteamId,
            teamId: team?._id
        })
        fetchIntegrations()
    }, [fetchIntegrations])

    // Also try fetching on mount regardless of dependencies
    useEffect(() => {
        console.log('[TeamIntegrationsContainer] Mount effect - fetching integrations')
        const fetchOnMount = async () => {
            try {
                console.log('[TeamIntegrationsContainer] Calling getIntegrations API...')
                const availableRes = await getIntegrations()
                console.log('[TeamIntegrationsContainer] Mount fetch result:', availableRes)
                console.log('[TeamIntegrationsContainer] Result type:', typeof availableRes)
                console.log('[TeamIntegrationsContainer] Is array?', Array.isArray(availableRes))

                let integrations = []
                if (Array.isArray(availableRes)) {
                    integrations = availableRes
                } else if (availableRes?.integrations && Array.isArray(availableRes.integrations)) {
                    integrations = availableRes.integrations
                } else if (availableRes?.data && Array.isArray(availableRes.data)) {
                    integrations = availableRes.data
                } else if (availableRes?.success && availableRes?.integrations) {
                    integrations = availableRes.integrations
                }

                console.log('[TeamIntegrationsContainer] Parsed integrations from mount:', integrations)
                console.log('[TeamIntegrationsContainer] Integrations count:', integrations.length)

                if (integrations.length > 0) {
                    setAvailableIntegrations(integrations)
                    console.log('[TeamIntegrationsContainer] Set available integrations:', integrations.length)
                } else {
                    console.warn('[TeamIntegrationsContainer] No integrations found in response!')
                    console.warn('[TeamIntegrationsContainer] Full response:', JSON.stringify(availableRes, null, 2))
                }
            } catch (error) {
                console.error('[TeamIntegrationsContainer] Mount fetch error:', error)
                console.error('[TeamIntegrationsContainer] Error stack:', error.stack)
                console.error('[TeamIntegrationsContainer] Error response:', error.response)
                setNotification({
                    open: true,
                    message: `Failed to load integrations: ${error.message || 'Unknown error'}`,
                    severity: 'error'
                })
            }
        }
        fetchOnMount()
    }, []) // Run once on mount

    const handleSearchChange = (newSearchTerm) => {
        setSearchTerm(newSearchTerm)
    }

    const handleIntegrationClick = async (integrationName) => {
        console.log('[TeamIntegrationsContainer] Clicked integration:', integrationName)
        console.log('[TeamIntegrationsContainer] Available integrations:', availableIntegrations)
        console.log('[TeamIntegrationsContainer] Loading state:', loading)

        if (loading) {
            setNotification({
                open: true,
                message: 'Please wait while integrations are loading...',
                severity: 'info'
            })
            return
        }

        if (availableIntegrations.length === 0) {
            setNotification({
                open: true,
                message: 'No integrations available. Please refresh the page.',
                severity: 'warning'
            })
            return
        }

        // Find the integration by name - try multiple variations
        let integration = availableIntegrations.find(
            i => {
                const nameMatch = i.name === integrationName || i.displayName === integrationName
                const zohoMatch = (i.name === 'Zoho CRM' || i.displayName === 'Zoho CRM') && integrationName === 'Zoho'
                return nameMatch || zohoMatch
            }
        )

        // Try case-insensitive match if not found
        if (!integration) {
            integration = availableIntegrations.find(
                i => i.name?.toLowerCase() === integrationName?.toLowerCase() ||
                    i.displayName?.toLowerCase() === integrationName?.toLowerCase()
            )
        }

        console.log('[TeamIntegrationsContainer] Found integration:', integration)

        if (!integration) {
            console.warn('[TeamIntegrationsContainer] Integration not found:', integrationName)
            console.log('[TeamIntegrationsContainer] Available names:', availableIntegrations.map(i => ({ name: i.name, displayName: i.displayName })))
            setNotification({
                open: true,
                message: `Integration "${integrationName}" not found. Please try again later.`,
                severity: 'warning'
            })
            return
        }

        // For OAuth integrations, initiate connection immediately
        if (integration.connectionType === 'oauth') {
            const teamId = currentTeamId || team?._id
            if (!teamId && !subteamId) {
                setNotification({
                    open: true,
                    message: 'Team ID not found. Please refresh the page.',
                    severity: 'error'
                })
                return
            }

            try {
                setConnecting(true)
                let response
                if (subteamId) {
                    response = await connectIntegration(subteamId, integration._id, {})
                } else {
                    response = await connectTeamIntegration(teamId, integration._id, {})
                }

                console.log('[TeamIntegrationsContainer] OAuth response:', response)

                if (response.authUrl) {
                    // Redirect to OAuth provider
                    window.location.href = response.authUrl
                } else {
                    setNotification({
                        open: true,
                        message: 'Failed to get OAuth URL',
                        severity: 'error'
                    })
                }
            } catch (error) {
                console.error('[TeamIntegrationsContainer] OAuth initiation error:', error)
                setNotification({
                    open: true,
                    message: error.response?.data?.message || 'Failed to connect integration',
                    severity: 'error'
                })
            } finally {
                setConnecting(false)
            }
        } else if (integration.connectionType === 'saml' || integration.connectionType === 'api_key') {
            // For SAML and API key integrations, show modal
            setSelectedIntegration(integration)
            setConnectionModalOpen(true)
        }
    }

    const handleConnectConfirm = async (connectionData = {}) => {
        const teamId = currentTeamId || team?._id
        if ((!teamId && !subteamId) || !selectedIntegration) return

        try {
            setConnecting(true)

            let response
            if (subteamId) {
                response = await connectIntegration(
                    subteamId,
                    selectedIntegration._id,
                    connectionData
                )
            } else {
                response = await connectTeamIntegration(
                    teamId,
                    selectedIntegration._id,
                    connectionData
                )
            }

            if (response.authUrl) {
                // OAuth flow - redirect to auth URL
                window.location.href = response.authUrl
            } else {
                // API key or direct connection
                setConnectionModalOpen(false)
                setNotification({
                    open: true,
                    message: `Successfully connected to ${selectedIntegration.displayName || selectedIntegration.name}!`,
                    severity: 'success'
                })
                await fetchIntegrations()
            }
        } catch (error) {
            console.error('Error connecting integration:', error)
            setNotification({
                open: true,
                message: error.response?.data?.message || 'Failed to connect integration',
                severity: 'error'
            })
        } finally {
            setConnecting(false)
        }
    }

    const handleDisconnect = async (integration) => {
        const teamId = currentTeamId || team?._id
        if ((!teamId && !subteamId) || !integration) return

        try {
            if (subteamId) {
                await disconnectIntegration(subteamId, integration._id)
            } else {
                await disconnectTeamIntegration(teamId, integration._id)
            }

            setNotification({
                open: true,
                message: `Successfully disconnected from ${integration.displayName || integration.name}`,
                severity: 'success'
            })
            await fetchIntegrations()
        } catch (error) {
            console.error('Error disconnecting integration:', error)
            setNotification({
                open: true,
                message: 'Failed to disconnect integration',
                severity: 'error'
            })
        }
    }

    const isIntegrationConnected = (integrationName) => {
        const integration = availableIntegrations.find(
            i => (i.name === integrationName || i.displayName === integrationName)
        )
        if (!integration) return false

        return connectedIntegrations.some(
            conn => {
                const connIntegrationId = conn.integrationId?._id || conn.integrationId
                return connIntegrationId?.toString() === integration._id.toString() && conn.status === 'connected'
            }
        )
    }

    const getConnectionStatus = (integrationName) => {
        const integration = availableIntegrations.find(
            i => (i.name === integrationName || i.displayName === integrationName)
        )
        if (!integration) return 'disconnected'

        const connection = connectedIntegrations.find(
            conn => {
                const connIntegrationId = conn.integrationId?._id || conn.integrationId
                return connIntegrationId?.toString() === integration._id.toString()
            }
        )
        return connection?.status || 'disconnected'
    }

    const handleConnectionModalClose = () => {
        setConnectionModalOpen(false)
        setSelectedIntegration(null)
    }

    const handleNotificationClose = () => {
        setNotification(prev => ({ ...prev, open: false }))
    }

    return {
        searchTerm,
        availableIntegrations,
        connectedIntegrations,
        loading,
        selectedIntegration,
        connectionModalOpen,
        connecting,
        notification,
        handleSearchChange,
        handleIntegrationClick,
        handleConnectConfirm,
        handleDisconnect,
        handleConnectionModalClose,
        handleNotificationClose,
        isIntegrationConnected,
        getConnectionStatus,
        fetchIntegrations
    }
}

